<template>
  <b-modal
    ref="modal"
    title="Import spreadsheet"
    centered
    size="lg"
    @hide="closeHandler"
  >
    <Multiselect
      v-model="selectedLayout"
      :options="layouts"
      :loading="isLoading"
      searchable
      multiple
      :custom-label="customLabel"
      track-by="id"
      class="mb-3"
      placeholder="Add to existing layout (optional)"
      @search-change="searchLayouts"
      @select="selectLayout"
    >
      <template
        v-if="layouts.length < layoutsTotalCount"
        #afterList
      >
        <div class="load-more-btn">
          <b-button
            :disabled="isLoading"
            variant="outline-primary"
            @click="getLayouts"
          >
            Load More
          </b-button>
        </div>
      </template>
    </Multiselect>

    <b-form-group
      v-if="!selectedLayout.length"
      label="New layout name"
      label-for="spreadsheet-layout-name"
    >
      <b-form-input
        id="spreadsheet-layout-name"
        v-model.trim="layoutName"
      />
    </b-form-group>

    <template #modal-footer>
      <div class="d-flex">
        <b-button @click="$refs.modal.hide()">
          Cancel
        </b-button>
        <div class="mx-1" />
        <b-button
          variant="primary"
          :disabled="!importAvailable || isImporting"
          @click="importSpreadsheet"
        >
          <b-spinner
            v-if="isImporting"
            small
          />
          Import
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import mixinModal from './mixin';
import LayoutAPI from '@frontend/services/api/layout';
import {
  SPREADSHEET_IMPORT_QUERY_PARAM,
  storePendingSpreadsheetImport
} from '@frontend/group/modules/import-spreadsheet-from-file-browser';

export default {
  name: 'ImportSpreadsheetModal',
  components: {
    Multiselect,
  },
  mixins: [mixinModal],
  props: {
    payload: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedLayout: [],
      layouts: [],
      layoutsTotalCount: 0,
      layoutName: '',
      page: 1,
      searchKey: null,
      isLoading: false,
      isImporting: false,
    };
  },
  computed: {
    importAvailable() {
      return Boolean(this.selectedLayout.length || this.layoutName);
    },
  },
  mounted() {
    window.addEventListener('pageshow', this.handlePageShow);
  },
  beforeDestroy() {
    window.removeEventListener('pageshow', this.handlePageShow);
  },
  methods: {
    handlePageShow() {
      this.isImporting = false;
    },
    async init() {
      this.selectedLayout = [];
      this.layouts = [];
      this.layoutsTotalCount = 0;
      this.layoutName = '';
      this.page = 1;
      this.searchKey = null;
      this.isImporting = false;
      this.$refs.modal.show();
      await this.getLayouts();
    },
    customLabel({ id, name }) {
      return `${id} - ${name}`;
    },
    selectLayout(layout) {
      this.selectedLayout = [layout];
    },
    async getLayouts() {
      this.isLoading = true;

      try {
        const { data: { layouts, totalCount } } = await LayoutAPI.getLayouts({
          params: {
            search: this.searchKey,
            page: this.page,
            limit: 20,
          },
        });

        this.layouts = this.layouts.concat(layouts);
        this.layoutsTotalCount = totalCount;
        this.page += 1;
      } catch (error) {
        console.error('Could not load layouts for spreadsheet import:', error);
        toastr.error('Could not load layouts.');
      } finally {
        this.isLoading = false;
      }
    },
    searchLayouts: _.debounce(function (search) {
      this.page = 1;
      this.searchKey = search;
      this.layouts = [];
      this.getLayouts();
    }, 500),
    async importSpreadsheet() {
      if (!this.importAvailable || this.isImporting) {
        return;
      }

      this.isImporting = true;

      try {
        let layout = this.selectedLayout[0];

        if (!layout) {
          const response = await LayoutAPI.createLayout(0, {
            name: this.layoutName,
            customer_id: 0,
          });
          layout = response.data;
        }

        storePendingSpreadsheetImport({
          id: this.payload.id,
          name: this.payload.name,
          path: this.payload.path,
          original_path: this.payload.original_path,
          full_url: this.payload.full_url,
          type: this.payload.type,
        });

        const customerId = layout.customer_id || 0;
        window.location.href = `/banner/${customerId}/group/${layout.id}/edit?${SPREADSHEET_IMPORT_QUERY_PARAM}=1`;
      } catch (error) {
        console.error('Could not start spreadsheet import:', error);
        toastr.error('Something went wrong while starting the spreadsheet import.');
        this.isImporting = false;
      }
    },
  },
};
</script>
